import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CalendarDays, Plus, RefreshCw, Loader2, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const PROVIDER_HELP = {
  google: <>Follow the steps to get your calendar URL <a href="https://support.google.com/calendar/answer/37082" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">here</a>.</>,
  outlook: <>Follow the steps to get your calendar URL <a href="https://support.microsoft.com/en-us/office/share-your-calendar-in-outlook-2fcf4f4f-8d46-4d8b-ae79-5d94549e531b" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">here</a>.</>,
  apple: <>Follow the steps to get your calendar URL <a href="https://support.apple.com/en-gb/guide/icloud/mm6b1a9479/icloud" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">here</a>.</>,
  other: 'Paste any valid .ics or iCal feed URL'
};

export const CalendarSyncCard = () => {
  const [calendarEnabled, setCalendarEnabled] = useState(false);
  const [calendarSubs, setCalendarSubs] = useState([]);
  const [showAddCalendar, setShowAddCalendar] = useState(false);
  const [newCalProvider, setNewCalProvider] = useState('google');
  const [newCalUrl, setNewCalUrl] = useState('');
  const [newCalLabel, setNewCalLabel] = useState('');
  const [addingCalendar, setAddingCalendar] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/calendar/subscriptions');
        setCalendarEnabled(res.data.enabled);
        setCalendarSubs(res.data.subscriptions);
      } catch (err) { console.debug('Calendar not set up yet:', err); }
    };
    fetch();
  }, []);

  const handleToggle = async (enabled) => {
    try {
      await api.put('/calendar/toggle', { enabled });
      setCalendarEnabled(enabled);
      toast.success(enabled ? 'Calendar sync enabled' : 'Calendar sync disabled');
    } catch { toast.error('Failed to update calendar setting'); }
  };

  const handleAdd = async () => {
    if (!newCalUrl.trim()) { toast.error('Please enter a calendar URL'); return; }
    setAddingCalendar(true);
    try {
      const res = await api.post('/calendar/subscriptions', {
        provider: newCalProvider, calendar_url: newCalUrl.trim(),
        label: newCalLabel.trim() || undefined
      });
      setCalendarSubs(prev => [...prev, res.data]);
      setCalendarEnabled(true);
      setNewCalUrl(''); setNewCalLabel(''); setShowAddCalendar(false);
      toast.success('Calendar connected!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add calendar');
    } finally { setAddingCalendar(false); }
  };

  const handleDelete = async (subId) => {
    try {
      await api.delete(`/calendar/subscriptions/${subId}`);
      setCalendarSubs(prev => prev.filter(s => s.subscription_id !== subId));
      toast.success('Calendar removed');
    } catch { toast.error('Failed to remove calendar'); }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/calendar/sync-now');
      toast.success(res.data.synced_events > 0
        ? `Synced ${res.data.synced_events} event${res.data.synced_events !== 1 ? 's' : ''} to your grid`
        : 'No events to sync for today');
    } catch { toast.error('Sync failed'); }
    finally { setSyncing(false); }
  };

  return (
    <Card className="glass border-white/20 dark:border-white/10" data-testid="calendar-sync-card">
      <CardHeader>
        <CardTitle className="font-['Manrope'] flex items-center gap-2">
          <CalendarDays className="w-5 h-5" /> Calendar Sync
        </CardTitle>
        <CardDescription>Pull today&apos;s calendar events into your grid at midnight each day</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Enable calendar sync</Label>
            <p className="text-sm text-muted-foreground">Events appear in the top-left quadrant daily at 00&#58;01</p>
          </div>
          <Switch checked={calendarEnabled} onCheckedChange={handleToggle} data-testid="calendar-sync-toggle" />
        </div>

        <Separator />

        {calendarSubs.length > 0 && (
          <div className="space-y-3">
            <Label className="text-sm font-medium">Connected calendars</Label>
            {calendarSubs.map((sub) => (
              <div key={sub.subscription_id} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <CalendarDays className="w-4 h-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{sub.label}</p>
                    <p className="text-xs text-muted-foreground capitalize">{sub.provider}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="flex-shrink-0 text-muted-foreground hover:text-destructive" onClick={() => handleDelete(sub.subscription_id)} data-testid={`delete-cal-${sub.subscription_id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {showAddCalendar ? (
          <div className="space-y-3 p-4 rounded-lg border border-border bg-muted/30">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Add a calendar</Label>
              <Button variant="ghost" size="icon" onClick={() => setShowAddCalendar(false)}><X className="w-4 h-4" /></Button>
            </div>
            <Select value={newCalProvider} onValueChange={setNewCalProvider}>
              <SelectTrigger data-testid="cal-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="google">Google Calendar</SelectItem>
                <SelectItem value="outlook">Microsoft Outlook</SelectItem>
                <SelectItem value="apple">Apple Calendar</SelectItem>
                <SelectItem value="other">Other (iCal)</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Paste your iCal/ICS feed URL" value={newCalUrl} onChange={(e) => setNewCalUrl(e.target.value)} data-testid="cal-url-input" />
            <Input placeholder="Label (optional)" value={newCalLabel} onChange={(e) => setNewCalLabel(e.target.value)} data-testid="cal-label-input" />
            <p className="text-xs text-muted-foreground leading-relaxed">{PROVIDER_HELP[newCalProvider]}</p>
            <Button onClick={handleAdd} disabled={addingCalendar || !newCalUrl.trim()} className="w-full" data-testid="save-calendar-btn">
              {addingCalendar ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {addingCalendar ? 'Validating...' : 'Connect Calendar'}
            </Button>
          </div>
        ) : (
          <Button variant="outline" onClick={() => setShowAddCalendar(true)} className="w-full gap-2" data-testid="add-calendar-btn">
            <Plus className="w-4 h-4" /> Add Calendar
          </Button>
        )}

        {calendarSubs.length > 0 && calendarEnabled && (
          <Button variant="outline" onClick={handleSyncNow} disabled={syncing} className="w-full gap-2" data-testid="sync-now-btn">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {syncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
